import React from 'react';
import { Check } from 'lucide-react';
import { EmailTemplate, HostlinksEvent } from '../../types';
import { templateSuggestedForEvent, templatesGroupedByKind } from './eventBuildUtils';

const MAX_TEMPLATES = 2;

interface Props {
  templates: EmailTemplate[];
  selectedIds: string[];
  onToggle: (template: EmailTemplate) => void;
  max?: number;
  event?: Pick<HostlinksEvent, 'type' | 'type_abbr'> | null;
}

const TemplateCardPicker: React.FC<Props> = ({
  templates,
  selectedIds,
  onToggle,
  max = MAX_TEMPLATES,
  event = null,
}) => {
  const atMax = selectedIds.length >= max;
  const groups = templatesGroupedByKind(templates);
  const anySuggested =
    !!event && templates.some((t) => templateSuggestedForEvent(t, event));

  const chipClass = (selected: boolean, disabled: boolean, suggested: boolean) => {
    if (selected) {
      return 'border-blue-500 bg-blue-600 text-white shadow-sm';
    }
    if (disabled) {
      return 'border-gray-200 dark:border-slate-700 text-gray-400 dark:text-slate-600 cursor-not-allowed';
    }
    if (suggested) {
      return 'border-amber-400 dark:border-amber-400 bg-amber-50 dark:bg-amber-950/40 text-amber-950 dark:text-amber-100 shadow-[0_0_10px_rgba(251,191,36,0.55)] ring-1 ring-amber-300 dark:ring-amber-500/80';
    }
    return 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 hover:border-blue-400 dark:hover:border-blue-500';
  };

  return (
    <div className="h-full bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 p-4 flex flex-col">
      <div className="mb-3">
        <p className="text-xs font-medium text-gray-700 dark:text-slate-200">
          Templates{' '}
          <span className="font-normal text-gray-500 dark:text-slate-400">(select up to {max})</span>
        </p>
        {anySuggested && (
          <p className="mt-0.5 text-[10px] text-amber-700 dark:text-amber-300">
            Amber glow = suggested for this event type (not selected until you click)
          </p>
        )}
      </div>

      {templates.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-slate-400">No templates available.</p>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <div key={group.kind}>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1.5">
                {group.label}
              </p>
              <div className="flex flex-wrap gap-2 content-start">
                {group.templates.map((template) => {
                  const selected = selectedIds.includes(template.id);
                  const order = selected ? selectedIds.indexOf(template.id) + 1 : null;
                  const disabled = !selected && atMax;
                  const suggested = !selected && templateSuggestedForEvent(template, event);
                  return (
                    <button
                      key={template.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => onToggle(template)}
                      title={
                        selected
                          ? `Selected as template ${order} — click to remove`
                          : disabled
                            ? `Maximum ${max} templates`
                            : suggested
                              ? `Suggested for this event — click to select ${template.name}`
                              : `Select ${template.name}`
                      }
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${chipClass(
                        selected,
                        disabled,
                        suggested
                      )}`}
                    >
                      {selected && <Check className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={2.5} />}
                      <span className="truncate max-w-[9rem]">{template.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {atMax && (
        <p className="mt-3 text-[11px] text-gray-500 dark:text-slate-400">
          Max {max} selected. Deselect one to pick another. Order = column order below.
        </p>
      )}
      {!atMax && selectedIds.length > 0 && (
        <p className="mt-3 text-[11px] text-gray-500 dark:text-slate-400">
          Selection order = column order below.
        </p>
      )}
    </div>
  );
};

export default TemplateCardPicker;
export { MAX_TEMPLATES };
